import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Mail, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { signup as apiSignup, login as apiLogin, resendInvitation, mfaChallenge, passkeyAuthBegin, passkeyAuthComplete } from "@/lib/api";
import { startAuthentication } from "@simplewebauthn/browser";
import Logo from "@/components/Logo";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const env = (process.env.REACT_APP_ENV || process.env.NODE_ENV || "").toLowerCase();
  const isProd = env === "prod" || env === "production";
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [modeParamSeen, setModeParamSeen] = useState(initialMode);
  if (modeParamSeen !== initialMode) {
    // params changed (back/forward nav) — sync without an effect
    setModeParamSeen(initialMode);
    setMode(initialMode);
  }
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaState, setMfaState] = useState(null); // { mfa_token, email } | null
  const [mfaCode, setMfaCode] = useState("");
  const [signupPendingState, setSignupPendingState] = useState(null); // { email, message }
  const [resendStatus, setResendStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "signup") {
        const res = await apiSignup({ email, password, full_name: fullName, organization_name: orgName });
        if (res?.pending) {
          setSignupPendingState({ email: res.email || email, message: res.message });
          setLoading(false);
          return;
        }
        setUser(res);
        await refresh();
        navigate("/app");
        return;
      }
      const res = await apiLogin({ email, password });
      if (res?.mfa_required) {
        setMfaState({ mfa_token: res.mfa_token, email: res.email });
        setLoading(false);
        return;
      }
      setUser(res);
      await refresh();
      if (res?.must_change_password || res?.mustChangePassword) {
        navigate("/change-password");
      } else {
        navigate("/app");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendSignupEmail = async () => {
    if (!signupPendingState?.email) return;
    setResendStatus(null);
    try {
      await resendInvitation(signupPendingState.email);
      setResendStatus("Invitation email resent successfully!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setResendStatus(typeof detail === "string" ? `Error: ${detail}` : "Failed to resend email.");
    }
  };

  const submitMfa = async () => {
    if (!mfaState) return;
    setError(null);
    setLoading(true);
    try {
      const user = await mfaChallenge(mfaState.mfa_token, mfaCode);
      setUser(user);
      await refresh();
      navigate("/app");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid code.");
    } finally {
      setLoading(false);
    }
  };

  const usePasskey = async () => {
    if (!email) { setError("Enter your email first."); return; }
    setError(null); setLoading(true);
    try {
      const opts = await passkeyAuthBegin(email);
      const cred = await startAuthentication(opts);
      const user = await passkeyAuthComplete(email, cred);
      setUser(user);
      await refresh();
      navigate("/app");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err?.message || "Passkey sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirect = `${window.location.origin}/auth/google/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative grain-overlay">
        <Link to="/" className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground text-sm">
          <ArrowLeft size={14} /> Back home
        </Link>
        <div className="space-y-6">
          <Logo height={56} />
          <h2 className="serif text-4xl leading-tight max-w-md">
            Stewardship is the new <em className="italic" style={{ color: "hsl(45 85% 55%)" }}>accountability</em>.
          </h2>
          <p className="text-primary-foreground/70 max-w-md text-sm leading-relaxed">
            Volunteers, treasurers, and board members — finally on the same page, with
            every receipt accounted for in seconds.
          </p>
        </div>
        <div className="text-xs text-primary-foreground/50">
          © {new Date().getFullYear()} TrustDime · Powered by{" "}
          <a
            href="https://www.itprofound.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-foreground/70 hover:text-primary-foreground underline-offset-2 hover:underline"
            data-testid="login-powered-by-itprofound"
          >
            ITProFound
          </a>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="serif text-3xl text-primary">
            {mode === "login" ? "Welcome back" : "Create your organization"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {mode === "login" ? "Sign in to continue stewarding well." : "Free Community plan. No credit card required."}
          </p>

          {error && (
            <div
              data-testid={AUTH.errorBanner}
              className="mt-6 rounded-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 text-sm"
            >
              {error}
            </div>
          )}

          {signupPendingState ? (
            <div className="mt-8 space-y-6 rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <Mail size={24} />
              </div>
              <div className="space-y-2">
                <h3 className="serif text-xl text-primary font-semibold">Check your email</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We've sent an organization activation email to <br />
                  <strong className="text-foreground font-mono text-xs px-2 py-0.5 rounded bg-background border">{signupPendingState.email}</strong>.
                </p>
                <p className="text-xs text-muted-foreground">
                  Please click the <strong>Accept Invitation</strong> link in that email to activate your account and log in.
                </p>
              </div>

              {resendStatus && (
                <div className="text-xs p-2.5 rounded bg-background border border-border text-foreground">
                  {resendStatus}
                </div>
              )}

              <div className="space-y-3 pt-2">
                <button
                  type="button"
                  onClick={handleResendSignupEmail}
                  className="w-full rounded-md border border-primary/30 bg-background hover:bg-accent text-primary text-xs font-medium py-2.5 transition-colors"
                >
                  Resend activation email
                </button>
                <button
                  type="button"
                  onClick={() => { setSignupPendingState(null); setMode("login"); }}
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          ) : mfaState ? (
            <div className="mt-8 space-y-4">
              <div className="rounded-sm border border-border bg-accent/40 px-4 py-3 text-sm">
                Enter the 6-digit code from your authenticator app for <span className="mono">{mfaState.email}</span>.
              </div>
              <Field label="Code">
                <input
                  data-testid="mfa-challenge-input"
                  type="text"
                  inputMode="numeric"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="td-input mono text-lg tracking-widest"
                  placeholder="123456"
                  maxLength={10}
                  autoFocus
                />
              </Field>
              <button
                data-testid="mfa-challenge-submit"
                onClick={submitMfa}
                disabled={loading || mfaCode.length < 6}
                className="w-full rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
              >
                {loading ? "Verifying…" : "Sign in"}
              </button>
              <button type="button" onClick={() => { setMfaState(null); setMfaCode(""); }} className="text-xs text-muted-foreground hover:text-foreground under-link">
                ← Back
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              {mode === "signup" && (
                <>
                  <Field label="Your full name">
                    <input
                      data-testid={AUTH.nameInput}
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="td-input"
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Organization name">
                    <input
                      data-testid={AUTH.orgInput}
                      type="text"
                      required
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="td-input"
                      placeholder="Hope Foundation"
                    />
                  </Field>
                </>
              )}
              <Field label="Email">
                <input
                  data-testid={AUTH.emailInput}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="td-input"
                  placeholder="you@org.com"
                />
              </Field>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-foreground">Password</label>
                  {mode === "login" && (
                    <Link
                      to={`/reset-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                      data-testid={AUTH.forgotPasswordLink}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>
                <input
                  data-testid={AUTH.passwordInput}
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="td-input"
                  placeholder="At least 8 characters"
                />
              </div>

              <button
                data-testid={AUTH.submitButton}
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {loading ? "Working…" : mode === "login" ? "Sign in" : "Create organization"}
                {!loading && <ArrowUpRight size={14} />}
              </button>
            </form>
          )}

          {/* <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <span>or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            data-testid={AUTH.googleButton}
            type="button"
            onClick={handleGoogle}
            className="w-full rounded-md border border-border bg-card hover:bg-accent transition-colors px-4 py-3 text-sm font-medium flex items-center justify-center gap-2"
          >
            <GoogleIcon /> Continue with Google
          </button> */}

          {/* {mode === "login" && !mfaState && (
            <button
              data-testid="passkey-login-button"
              type="button"
              onClick={usePasskey}
              className="mt-3 w-full rounded-md border border-border bg-card hover:bg-accent transition-colors px-4 py-3 text-sm font-medium flex items-center justify-center gap-2"
            >
              Sign in with a passkey
            </button>
          )} */}

          {mode === "login" && !isProd && (
            <DemoAccounts
              onPick={(creds) => { setEmail(creds.email); setPassword(creds.password); setError(null); }}
              onSignIn={async (creds) => {
                setEmail(creds.email); setPassword(creds.password);
                setError(null); setLoading(true);
                try {
                  const res = await apiLogin({ email: creds.email, password: creds.password });
                  if (res?.mfa_required) {
                    setMfaState({ mfa_token: res.mfa_token, email: res.email });
                  } else {
                    setUser(res); await refresh();
                    navigate(res?.user?.role === "donor" ? "/donor-portal" : "/app");
                  }
                } catch (err) {
                  setError(err?.response?.data?.detail || "Demo sign-in failed.");
                } finally { setLoading(false); }
              }}
            />
          )}

          <button
            data-testid={AUTH.switchMode}
            onClick={() => setMode(mode === "login" ? "signup" : "login")}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground"
          >
            {mode === "login"
              ? "New here? Create an organization →"
              : "Already have an account? Sign in →"}
          </button>
        </div>
      </div>
      <style>{`
        .td-input {
          width: 100%;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 14px;
          color: hsl(var(--foreground));
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 11v3.13h7.34c-.3 1.94-2.13 5.69-7.34 5.69-4.42 0-8.02-3.66-8.02-8.18S7.58 3.46 12 3.46c2.5 0 4.18 1.06 5.14 1.97l3.5-3.39C18.4.79 15.5 0 12 0 5.37 0 0 5.37 0 12s5.37 12 12 12c6.92 0 11.5-4.86 11.5-11.7 0-.79-.08-1.39-.18-1.97L12 11z" />
    </svg>
  );
}

const DEMO_ACCOUNTS = [
  {
    section: "Platform Admins",
    tone: "platform",
    accounts: [
      { label: "Super Admin", email: "super@trustdime.io", password: "Pass123!", role: "super_admin", hint: "Mission Control · god mode" },
      { label: "Regional Admin", email: "regional.na@trustdime.io", password: "Pass123!", role: "regional_admin", hint: "North America region" },
      { label: "Financials Admin", email: "finance@trustdime.io", password: "Pass123!", role: "financials_admin", hint: "Stripe · pricing · promos" },
    ],
  },
  {
    section: "Hope Harbor Food Pantry (Primary User Roles)",
    tone: "org",
    accounts: [
      { label: "Executive Leadership", email: "executive@hopeharborfoodpantry.demo", password: "Pass123!", role: "EXECUTIVE", hint: "Organizational dashboard & governance" },
      { label: "Development & Fundraising", email: "dev.lead@hopeharborfoodpantry.demo", password: "Pass123!", role: "DEVELOPMENT_STAFF", hint: "Donor CRM & pledge pipeline" },
      { label: "Program & Outreach Leader", email: "program.lead@hopeharborfoodpantry.demo", password: "Pass123!", role: "PROGRAM_LEADER", hint: "Events & encrypted case notes" },
      { label: "Finance & Admin Staff", email: "jules.park@hopeharborfoodpantry.demo", password: "Pass123!", role: "FINANCE_STAFF", hint: "Receipts & QuickBooks exports" },
      { label: "Volunteer Coordinator", email: "coordinator@hopeharborfoodpantry.demo", password: "Pass123!", role: "VOLUNTEER_COORDINATOR", hint: "Volunteer rosters & Checkr background checks" },
      { label: "Volunteer (Self-Service)", email: "volunteer@hopeharborfoodpantry.demo", password: "Pass123!", role: "VOLUNTEER", hint: "Shift registration & hours tracking" },
      { label: "Board Member", email: "board.member@hopeharborfoodpantry.demo", password: "Pass123!", role: "BOARD_MEMBER", hint: "Board packets & resolution voting" },
      { label: "Beneficiary / Aid Applicant", email: "beneficiary@hopeharborfoodpantry.demo", password: "Pass123!", role: "BENEFICIARY", hint: "Sandboxed aid portal & documents" },
      { label: "Organization Admin", email: "casey.bridges@hopeharborfoodpantry.demo", password: "Pass123!", role: "organization_admin", hint: "Org owner" },
    ],
  },
];

function DemoAccounts({ onPick, onSignIn }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-8 rounded-xl border-2 border-secondary/40 bg-secondary/5 p-4" data-testid="demo-accounts-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="demo-accounts-toggle"
        className="w-full flex items-center justify-between text-sm hover:text-foreground"
      >
        <span className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground font-medium">demo</span>
          <span className="font-medium">Try TrustDime with a seeded account</span>
        </span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          {DEMO_ACCOUNTS.map((group) => (
            <div key={group.section}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
                {group.section}
              </div>
              <div className="space-y-1.5">
                {group.accounts.map((a) => (
                  <div
                    key={a.email}
                    data-testid={`demo-account-${a.role}`}
                    className="group flex items-center justify-between gap-2 text-xs rounded-md border border-border bg-card hover:bg-accent/50 transition-colors px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate">{a.label}</span>
                        <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 rounded ${group.tone === "platform" ? "bg-amber-100 text-amber-900" : "bg-primary/10 text-primary"
                          }`}>
                          {a.role.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="text-muted-foreground truncate font-mono text-[11px]">{a.email}</div>
                      <div className="text-muted-foreground/70 text-[10px]">{a.hint}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => onPick(a)}
                        data-testid={`demo-fill-${a.role}`}
                        className="text-[11px] px-2 py-1 rounded border border-border hover:bg-background"
                        title="Fill the form"
                      >
                        Fill
                      </button>
                      <button
                        type="button"
                        onClick={() => onSignIn(a)}
                        data-testid={`demo-signin-${a.role}`}
                        className="text-[11px] px-2 py-1 rounded bg-primary text-primary-foreground hover:opacity-90"
                        title="Sign in as this user"
                      >
                        Sign in →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground/80">
            All demo accounts use password <code className="font-mono">Pass123!</code>. More demo orgs (Brightline, Riverstone, Caritas, Pacific Reef) use the same naming pattern with their own domains.
          </p>
        </div>
      )}
    </div>
  );
}
